import { CrewUi, enumToZodLiteralUnion } from 'corso-types';

import { z } from 'zod';
import { zfd } from 'zod-form-data';
import {
  ClaimReasonCategoryCode,
  ClaimType,
  MediaType,
  ResolutionMethod,
} from './constants';

export const {
  claimAddressSchema,
  orderAddressSchema,
  shipmentCreateSchema,
  shipmentMethodSchema,
  shipmentRateSchema,
  claimLineItemCreateSchema,
  gspOrderSchema,
  shippingClaimSchema,
  settingsSchema,
  registrationSchema,
  claimSchema,
  locationAddressSchema,
} = CrewUi;

export const typeGuardFromSchema =
  <Output, Def extends z.ZodTypeDef, Input>(
    schema: z.ZodType<Output, Def, Input>,
  ) =>
  (data: unknown): data is Input =>
    schema.safeParse(data).success;

const errorMessage = z.object({
  message: z.string(),
});

export const isErrorMessage = (
  obj: unknown,
): obj is z.infer<typeof errorMessage> => errorMessage.safeParse(obj).success;

export function isApiError(
  body: unknown,
): body is z.infer<typeof errorMessage> {
  return errorMessage.safeParse(body).success;
}

export const mediaAssetSchema = z.object({
  src: z.string(),
  name: z.string(),
  type: z.nativeEnum(MediaType),
});

export const stringToJSONSchema = z.string().transform((str, ctx) => {
  try {
    return JSON.parse(str);
  } catch (e) {
    ctx.addIssue({ code: 'custom', message: 'Invalid JSON' });
    return z.NEVER;
  }
});

export const addressSchema = z.object({
  firstName: zfd.text(z.string({ message: 'First name is required.' })),
  lastName: zfd.text(z.string({ message: 'Last name is required.' })),
  line1: zfd.text(z.string({ message: 'Address is required.' })),
  line2: zfd.text(z.string().optional()),
  city: zfd.text(z.string({ message: 'City is required.' })),
  stateOrProvinceCode: zfd.text(
    claimAddressSchema.shape.stateOrProvinceCode.optional(),
  ),
  postalCode: zfd.text(z.string({ message: 'Postal Code is required.' })),
  countryCode: claimAddressSchema.shape.countryCode,
  phone: zfd.text(z.string().optional()),
});

export const crewOrderLineItemSchema = CrewUi.crewOrderLineItemSchema.extend({
  remainingReturnableQuantity: z.number(),
  unitPriceDisplay: z.string(),
});

const lineItemClaimReasonSchema = z.object({
  id: z.number(),
  name: z.string(),
  category: enumToZodLiteralUnion(ClaimReasonCategoryCode),
  detail: z
    .object({
      id: z.number(),
      name: z.string(),
    })
    .optional(),
});

export const exchangeVariantSchema = CrewUi.variantExchangeOptionsSchema;

const customFields = z.record(
  z.object({
    value: z.string(),
    type: z.union([
      CrewUi.customFieldSelectSchema.shape.valueType,
      CrewUi.customFieldGeneralSchema.shape.valueType,
    ]),
  }),
);

export const draftLineItemClaimSchema = CrewUi.claimLineItemCreateSchema
  .omit({
    images: true,
    reason: true,
    customFields: true,
    requestedResolutionMethodEnum: true,
  })
  .extend({
    id: z.string(),
    claimType: z.nativeEnum(ClaimType),
    lineItem: crewOrderLineItemSchema,
    quantity: z.number(),
    variant: exchangeVariantSchema.optional(),
    unitPrice: z.number(),
    unitPriceDisplay: z.string(),
    unitTax: z.number(),
    unitListPrice: z.number().optional(),
    customFields,
    grams: z.number(),
    assets: z.array(mediaAssetSchema).optional(),
    requestedResolutionMethodEnum: z
      .enum([
        ResolutionMethod.refund,
        ResolutionMethod.variantExchange,
        ResolutionMethod.giftCard,
        ResolutionMethod.warrantyReview,
      ])
      .optional(),
    reason: lineItemClaimReasonSchema.optional(),
  });

export const variantExchangeClaimSchema = draftLineItemClaimSchema
  .omit({ requestedResolutionMethodEnum: true })
  .extend({
    requestedResolutionMethodEnum: z.literal(ResolutionMethod.variantExchange),
    variant: CrewUi.variantExchangeOptionsSchema,
    reason: lineItemClaimReasonSchema,
    claimType: z.literal(ClaimType.return),
  });

export const refundClaimSchema = draftLineItemClaimSchema
  .omit({ requestedResolutionMethodEnum: true })
  .extend({
    requestedResolutionMethodEnum: z.literal(ResolutionMethod.refund),

    variant: z.never().optional(),
    reason: lineItemClaimReasonSchema,
    claimType: z.literal(ClaimType.return),
  });

export const giftCardClaimSchema = draftLineItemClaimSchema
  .omit({ requestedResolutionMethodEnum: true })
  .extend({
    requestedResolutionMethodEnum: z.literal(ResolutionMethod.giftCard),
    variant: z.never().optional(),
    reason: lineItemClaimReasonSchema,
    claimType: z.literal(ClaimType.return),
  });

const warrantyClaimSchema = draftLineItemClaimSchema
  .omit({ requestedResolutionMethodEnum: true })
  .extend({
    requestedResolutionMethodEnum: z.literal(ResolutionMethod.warrantyReview),
    variant: z.never().optional(),
    reason: lineItemClaimReasonSchema,
    claimType: z.literal(ClaimType.warranty),
  });

export const lineItemClaimSchema = z.discriminatedUnion(
  'requestedResolutionMethodEnum',
  [
    variantExchangeClaimSchema,
    refundClaimSchema,
    giftCardClaimSchema,
    warrantyClaimSchema,
  ],
);

export const crewOrderSchema = CrewUi.orderSchema.extend({
  lineItems: z.array(crewOrderLineItemSchema),
});

export const crewStateSchema = z.object({
  settings: CrewUi.settingsSchema,
  claim: CrewUi.claimSchema.nullable(),
  draftClaim: draftLineItemClaimSchema.nullable(),
  lineItemClaims: z.array(lineItemClaimSchema),
  order: crewOrderSchema.nullable(),
  address: CrewUi.claimAddressSchema.nullable(),
  toCustomerRate: CrewUi.shipmentRateSchema.nullable(),
  returnMethod: CrewUi.shipmentMethodSchema.nullable(),
});

const storeSalesChannelSchema = z.object({
  id: z.number(),
  name: z.string(),
});

export const draftRegistrationSchema = z.object({
  email: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  storeSalesChannel: storeSalesChannelSchema,
  estimatedPurchaseDate: z.string(),
  address: z.nullable(CrewUi.claimAddressSchema),
  proofOfPurchaseAssets: z.array(mediaAssetSchema).nullable(),
  orderNumber: z.string().optional(),
  referrer: z.string().optional(),
  customFields,
});

export const storeParamSchema = z.object({
  store: z
    .string()
    .min(1)
    .refine(
      (store) => {
        // * These are first level paths in the app that should not be used as store names for instance /order/order/:orderId
        // really don't like having this here vs with the router, but it is the central path for the app
        const invalidKeywords = [
          'order',
          'claim',
          'inactive',
          'register',
          'reorder',
        ];

        return !invalidKeywords.includes(store);
      },
      {
        message: 'The store name must not be a reserved keyword.',
      },
    ),
});
